import React, { useState } from 'react';
import { motion } from 'framer-motion';
import LiquidGlassInput from './Input';

const InputDemo = () => {
  const [formData, setFormData] = useState({
    fullname: { firstname: '', lastname: '' },
    email: '',
    password: '',
    message: '',
    search: ''
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState({});
  const [specialInputs, setSpecialInputs] = useState({
    email: '',
    password: '',
    number: '',
    phone: '',
    fullname: { firstname: '', lastname: '' }
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Validate email
    if (field === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      } else {
        setSuccess(prev => ({ ...prev, email: true }));
      }
    }
    
    // Validate password
    if (field === 'password' && value) {
      if (value.length < 6) {
        setErrors(prev => ({ ...prev, password: 'Password must be at least 6 characters' }));
      } else {
        setSuccess(prev => ({ ...prev, password: true }));
      }
    }
  };

  const handleSpecialInputChange = (field, value) => {
    setSpecialInputs(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: `
        linear-gradient(135deg, #667eea 0%, #764ba2 100%),
        radial-gradient(circle at 20% 80%, #f093fb 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, #f5576c 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, #4facfe 0%, transparent 50%)
      `,
      backgroundBlendMode: 'overlay, screen, screen, screen',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '40px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        style={{
          textAlign: 'center',
          color: 'white',
          marginBottom: '20px'
        }}
      >
        <h1 style={{
          fontSize: '3rem',
          fontWeight: 'bold',
          margin: '0 0 10px 0',
          textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          background: 'linear-gradient(45deg, #fff, #f0f0f0)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Liquid Glass Input
        </h1>
        <p style={{
          fontSize: '1.2rem',
          opacity: 0.9,
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Advanced glass morphism input fields with validation
        </p>
      </motion.div>

      {/* Variants Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '800px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Input Variants
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          width: '100%'
        }}>
          <LiquidGlassInput
            variant="primary"
            label="Primary Input"
            placeholder="Enter your text..."
            value={formData.fullname.firstname}
            onChange={(e) => handleInputChange('fullname', { ...formData.fullname, firstname: e.target.value })}
          />
          <LiquidGlassInput
            variant="secondary"
            label="Secondary Input"
            placeholder="Secondary style..."
            value={formData.search}
            onChange={(e) => handleInputChange('search', e.target.value)}
          />
          <LiquidGlassInput
            variant="success"
            label="Success Input"
            placeholder="Success state..."
            value="Valid input"
            success={true}
          />
          <LiquidGlassInput
            variant="danger"
            label="Error Input"
            placeholder="Error state..."
            value="Invalid input"
            error={true}
            helperText="This field has an error"
          />
        </div>
      </motion.div>

      {/* Sizes Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '800px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Input Sizes
        </h2>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          width: '100%'
        }}>
          <LiquidGlassInput
            size="small"
            label="Small Input"
            placeholder="Small size..."
            value={formData.search}
            onChange={(e) => handleInputChange('search', e.target.value)}
          />
          <LiquidGlassInput
            size="medium"
            label="Medium Input (Default)"
            placeholder="Medium size..."
            value={formData.fullname.firstname}
            onChange={(e) => handleInputChange('fullname', { ...formData.fullname, firstname: e.target.value })}
          />
          <LiquidGlassInput
            size="large"
            label="Large Input"
            placeholder="Large size..."
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
          />
        </div>
      </motion.div>

      {/* Interactive Form */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '800px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Interactive Form
        </h2>
        <motion.form
          onSubmit={handleSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            width: '100%',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: '30px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
          whileHover={{
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
          }}
          transition={{ duration: 0.3 }}
        >
          <LiquidGlassInput
            type="fullname"
            label="Full Name"
            placeholder={{ firstname: 'First name', lastname: 'Last name' }}
            value={formData.fullname}
            onChange={e => handleInputChange('fullname', e.target.value)}
            required={true}
            helperText="Please enter your complete name"
          />
          
          <LiquidGlassInput
            type="email"
            label="Email Address"
            placeholder="Enter your email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={!!errors.email}
            success={success.email}
            helperText={errors.email || (success.email ? 'Email is valid!' : 'We\'ll never share your email')}
            required={true}
          />
          
          <LiquidGlassInput
            type="password"
            label="Password"
            placeholder="Enter your password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            error={!!errors.password}
            success={success.password}
            helperText={errors.password || (success.password ? 'Password is strong!' : 'Minimum 6 characters')}
            required={true}
          />
          
          <LiquidGlassInput
            label="Message"
            placeholder="Enter your message..."
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            helperText="Tell us what you think"
          />
          
          <motion.button
            type="submit"
            style={{
              background: `
                linear-gradient(135deg, 
                  rgba(255, 255, 255, 0.1) 0%, 
                  rgba(255, 255, 255, 0.05) 50%, 
                  rgba(255, 255, 255, 0.1) 100%
                ),
                radial-gradient(
                  circle at 30% 30%, 
                  rgba(255, 255, 255, 0.2) 0%, 
                  transparent 50%
                )
              `,
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '16px',
              color: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              cursor: 'pointer',
              outline: 'none',
              fontWeight: 500,
              letterSpacing: '0.5px',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
            }}
            whileHover={{
              scale: 1.02,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
            }}
            whileTap={{
              scale: 0.98
            }}
            transition={{ duration: 0.2 }}
          >
            Submit Form
          </motion.button>
        </motion.form>
      </motion.div>

      {/* Special Input Types */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.8 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '800px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Special Input Types
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          width: '100%'
        }}>
          <LiquidGlassInput
            type="fullname"
            label="Full Name"
            placeholder={{ firstname: 'First name', lastname: 'Last name' }}
            value={specialInputs.fullname}
            onChange={e => handleSpecialInputChange('fullname', e.target.value)}
            helperText="Enter your first and last name"
          />
          <div style={{ color: 'white', fontSize: '1rem', marginTop: 8 }}>
            <div>First: {specialInputs.fullname.firstname}</div>
            <div>Last: {specialInputs.fullname.lastname}</div>
          </div>
          <LiquidGlassInput
            type="email"
            label="Email Input"
            placeholder="Enter email address"
            helperText="With email validation"
            value={specialInputs.email}
            onChange={e => handleSpecialInputChange('email', e.target.value)}
          />
          <LiquidGlassInput
            type="password"
            label="Password Input"
            placeholder="Enter password"
            helperText="Secure password field"
            value={specialInputs.password}
            onChange={e => handleSpecialInputChange('password', e.target.value)}
          />
          <LiquidGlassInput
            type="number"
            label="Number Input"
            placeholder="Enter a number"
            helperText="Numeric input only"
            value={specialInputs.number}
            onChange={e => handleSpecialInputChange('number', e.target.value)}
          />
          <LiquidGlassInput
            type="tel"
            label="Phone Input"
            placeholder="Enter phone number"
            helperText="Phone number format"
            value={specialInputs.phone}
            onChange={e => handleSpecialInputChange('phone', e.target.value)}
          />
        </div>
      </motion.div>

      {/* Disabled State */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.0 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '800px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Disabled State
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          width: '100%'
        }}>
          <LiquidGlassInput
            label="Disabled Input"
            placeholder="This input is disabled"
            value="Disabled value"
            disabled={true}
            helperText="This field cannot be edited"
          />
          <LiquidGlassInput
            label="Read Only Input"
            placeholder="This input is read-only"
            value="Read-only value"
            readOnly={true}
            helperText="This field is read-only"
          />
        </div>
      </motion.div>

      {/* Features List */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '800px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Input Features
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          width: '100%'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Glass Effects</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>backdrop-filter: blur(20px)</li>
              <li>rgba/hsla backgrounds</li>
              <li>background-blend-mode</li>
              <li>Glass borders & shadows</li>
              <li>Focus ring effects</li>
            </ul>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Animations</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>Parallax mouse tracking</li>
              <li>Focus animations</li>
              <li>Label animations</li>
              <li>Status indicators</li>
              <li>Smooth transitions</li>
            </ul>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Validation</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>Error states</li>
              <li>Success states</li>
              <li>Helper text</li>
              <li>Required fields</li>
              <li>Real-time validation</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default InputDemo; 